import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Database,
  Columns,
  Filter,
  SortAsc,
  SortDesc,
  Play,
  Save,
  Download,
  Loader2,
  FileSpreadsheet,
  FileText,
  FileJson,
  ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import ReportTable from './ReportTable';

interface DataSourceConfig {
  name: string;
  label: string;
  columns: { key: string; label: string; type: string }[];
}

const dataSources: DataSourceConfig[] = [
  {
    name: 'assets',
    label: 'Assets',
    columns: [
      { key: 'assetTag', label: 'Asset Tag', type: 'string' },
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'serialNumber', label: 'Serial Number', type: 'string' },
      { key: 'manufacturer', label: 'Manufacturer', type: 'string' },
      { key: 'model', label: 'Model', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'condition', label: 'Condition', type: 'string' },
      { key: 'purchaseCost', label: 'Purchase Cost', type: 'number' },
      { key: 'currentValue', label: 'Current Value', type: 'number' },
      { key: 'depreciationValue', label: 'Depreciation', type: 'number' },
      { key: 'purchaseDate', label: 'Purchase Date', type: 'date' },
      { key: 'warrantyExpiry', label: 'Warranty Expiry', type: 'date' },
      { key: 'deploymentDate', label: 'Deployment Date', type: 'date' },
      { key: 'lastAuditDate', label: 'Last Audit Date', type: 'date' },
      { key: 'category.name', label: 'Category', type: 'string' },
      { key: 'brand.name', label: 'Brand', type: 'string' },
      { key: 'branch.name', label: 'Branch', type: 'string' },
      { key: 'location.name', label: 'Location', type: 'string' },
      { key: 'vendor.name', label: 'Vendor', type: 'string' },
      { key: 'assignedTo.firstName', label: 'Assigned To (First)', type: 'string' },
      { key: 'assignedTo.lastName', label: 'Assigned To (Last)', type: 'string' },
      { key: 'assignedTo.employeeId', label: 'Assigned To (ID)', type: 'string' },
      { key: 'isLoaner', label: 'Is Loaner', type: 'boolean' },
      { key: 'isMarked', label: 'Is Marked', type: 'boolean' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'employees',
    label: 'Employees',
    columns: [
      { key: 'employeeId', label: 'Employee ID', type: 'string' },
      { key: 'firstName', label: 'First Name', type: 'string' },
      { key: 'lastName', label: 'Last Name', type: 'string' },
      { key: 'email', label: 'Email', type: 'string' },
      { key: 'phone', label: 'Phone', type: 'string' },
      { key: 'jobTitle', label: 'Job Title', type: 'string' },
      { key: 'employmentType', label: 'Employment Type', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'hireDate', label: 'Hire Date', type: 'date' },
      { key: 'terminationDate', label: 'Termination Date', type: 'date' },
      { key: 'department.name', label: 'Department', type: 'string' },
      { key: 'branch.name', label: 'Branch', type: 'string' },
      { key: '_count.assets', label: 'Asset Count', type: 'number' },
      { key: '_count.repairRecords', label: 'Repair Count', type: 'number' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'repairs',
    label: 'Repairs',
    columns: [
      { key: 'repairNumber', label: 'Repair Number', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'repairType', label: 'Type', type: 'string' },
      { key: 'problemDescription', label: 'Problem', type: 'string' },
      { key: 'diagnosis', label: 'Diagnosis', type: 'string' },
      { key: 'repairNotes', label: 'Repair Notes', type: 'string' },
      { key: 'estimatedCost', label: 'Estimated Cost', type: 'number' },
      { key: 'actualCost', label: 'Actual Cost', type: 'number' },
      { key: 'warrantyRepair', label: 'Warranty Repair', type: 'boolean' },
      { key: 'sentDate', label: 'Sent Date', type: 'date' },
      { key: 'expectedReturnDate', label: 'Expected Return', type: 'date' },
      { key: 'completedDate', label: 'Completed Date', type: 'date' },
      { key: 'asset.assetTag', label: 'Asset Tag', type: 'string' },
      { key: 'asset.name', label: 'Asset Name', type: 'string' },
      { key: 'asset.serialNumber', label: 'Serial Number', type: 'string' },
      { key: 'employee.firstName', label: 'Employee First Name', type: 'string' },
      { key: 'employee.lastName', label: 'Employee Last Name', type: 'string' },
      { key: 'serviceProvider.name', label: 'Service Provider', type: 'string' },
      { key: 'takenBy.firstName', label: 'Taken By', type: 'string' },
      { key: 'receivedBy.firstName', label: 'Received By', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'consumables',
    label: 'Consumables',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'partNumber', label: 'Part Number', type: 'string' },
      { key: 'description', label: 'Description', type: 'string' },
      { key: 'minimumStockLevel', label: 'Minimum Stock', type: 'number' },
      { key: 'unitCost', label: 'Unit Cost', type: 'number' },
      { key: 'unit', label: 'Unit', type: 'string' },
      { key: 'category.name', label: 'Category', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'tickets',
    label: 'Tickets',
    columns: [
      { key: 'ticketNumber', label: 'Ticket Number', type: 'string' },
      { key: 'title', label: 'Title', type: 'string' },
      { key: 'description', label: 'Description', type: 'string' },
      { key: 'type', label: 'Type', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'priority', label: 'Priority', type: 'string' },
      { key: 'source', label: 'Source', type: 'string' },
      { key: 'slaFirstResponseDue', label: 'SLA First Response', type: 'date' },
      { key: 'slaResolutionDue', label: 'SLA Resolution Due', type: 'date' },
      { key: 'resolvedAt', label: 'Resolved At', type: 'date' },
      { key: 'closedAt', label: 'Closed At', type: 'date' },
      { key: 'asset.assetTag', label: 'Asset', type: 'string' },
      { key: 'requester.firstName', label: 'Requester First Name', type: 'string' },
      { key: 'requester.lastName', label: 'Requester Last Name', type: 'string' },
      { key: 'assignee.firstName', label: 'Assignee First Name', type: 'string' },
      { key: 'assignee.lastName', label: 'Assignee Last Name', type: 'string' },
      { key: 'category.name', label: 'Category', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'branches',
    label: 'Branches',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'code', label: 'Code', type: 'string' },
      { key: 'address', label: 'Address', type: 'string' },
      { key: 'city', label: 'City', type: 'string' },
      { key: 'country', label: 'Country', type: 'string' },
      { key: 'phone', label: 'Phone', type: 'string' },
      { key: 'email', label: 'Email', type: 'string' },
      { key: 'isActive', label: 'Is Active', type: 'boolean' },
      { key: '_count.assets', label: 'Asset Count', type: 'number' },
      { key: '_count.employees', label: 'Employee Count', type: 'number' },
      { key: '_count.users', label: 'User Count', type: 'number' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'departments',
    label: 'Departments',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'code', label: 'Code', type: 'string' },
      { key: 'description', label: 'Description', type: 'string' },
      { key: 'isActive', label: 'Is Active', type: 'boolean' },
      { key: '_count.employees', label: 'Employee Count', type: 'number' },
      { key: '_count.users', label: 'User Count', type: 'number' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'vendors',
    label: 'Vendors',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'code', label: 'Code', type: 'string' },
      { key: 'contactPerson', label: 'Contact Person', type: 'string' },
      { key: 'email', label: 'Email', type: 'string' },
      { key: 'phone', label: 'Phone', type: 'string' },
      { key: 'address', label: 'Address', type: 'string' },
      { key: 'website', label: 'Website', type: 'string' },
      { key: 'taxId', label: 'Tax ID', type: 'string' },
      { key: 'paymentTerms', label: 'Payment Terms', type: 'string' },
      { key: 'rating', label: 'Rating', type: 'number' },
      { key: 'isActive', label: 'Is Active', type: 'boolean' },
      { key: '_count.assets', label: 'Asset Count', type: 'number' },
      { key: '_count.purchaseOrders', label: 'PO Count', type: 'number' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'serviceProviders',
    label: 'Service Providers',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'contactPerson', label: 'Contact Person', type: 'string' },
      { key: 'email', label: 'Email', type: 'string' },
      { key: 'phone', label: 'Phone', type: 'string' },
      { key: 'address', label: 'Address', type: 'string' },
      { key: 'specializations', label: 'Specializations', type: 'string' },
      { key: 'rating', label: 'Rating', type: 'number' },
      { key: 'isActive', label: 'Is Active', type: 'boolean' },
      { key: '_count.repairRecords', label: 'Repair Count', type: 'number' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'documents',
    label: 'Documents',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'type', label: 'Type', type: 'string' },
      { key: 'description', label: 'Description', type: 'string' },
      { key: 'fileSize', label: 'File Size', type: 'number' },
      { key: 'mimeType', label: 'MIME Type', type: 'string' },
      { key: 'expiryDate', label: 'Expiry Date', type: 'date' },
      { key: 'asset.assetTag', label: 'Asset Tag', type: 'string' },
      { key: 'asset.name', label: 'Asset Name', type: 'string' },
      { key: 'serviceProvider.name', label: 'Service Provider', type: 'string' },
      { key: 'uploadedBy.firstName', label: 'Uploaded By', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'categories',
    label: 'Asset Categories',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'code', label: 'Code', type: 'string' },
      { key: 'description', label: 'Description', type: 'string' },
      { key: 'depreciationMethod', label: 'Depreciation Method', type: 'string' },
      { key: 'usefulLifeYears', label: 'Useful Life (Years)', type: 'number' },
      { key: 'parent.name', label: 'Parent Category', type: 'string' },
      { key: '_count.assets', label: 'Asset Count', type: 'number' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'brands',
    label: 'Brands',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'description', label: 'Description', type: 'string' },
      { key: 'website', label: 'Website', type: 'string' },
      { key: '_count.assets', label: 'Asset Count', type: 'number' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'licenses',
    label: 'Software Licenses',
    columns: [
      { key: 'name', label: 'Name', type: 'string' },
      { key: 'licenseKey', label: 'License Key', type: 'string' },
      { key: 'licenseType', label: 'License Type', type: 'string' },
      { key: 'seats', label: 'Seats', type: 'number' },
      { key: 'seatsUsed', label: 'Seats Used', type: 'number' },
      { key: 'purchaseDate', label: 'Purchase Date', type: 'date' },
      { key: 'expiryDate', label: 'Expiry Date', type: 'date' },
      { key: 'cost', label: 'Cost', type: 'number' },
      { key: 'vendor.name', label: 'Vendor', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'purchaseOrders',
    label: 'Purchase Orders',
    columns: [
      { key: 'poNumber', label: 'PO Number', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'priority', label: 'Priority', type: 'string' },
      { key: 'vendorName', label: 'Vendor Name', type: 'string' },
      { key: 'vendorContact', label: 'Vendor Contact', type: 'string' },
      { key: 'vendorEmail', label: 'Vendor Email', type: 'string' },
      { key: 'subtotal', label: 'Subtotal', type: 'number' },
      { key: 'taxAmount', label: 'Tax Amount', type: 'number' },
      { key: 'totalAmount', label: 'Total Amount', type: 'number' },
      { key: 'currency', label: 'Currency', type: 'string' },
      { key: 'orderDate', label: 'Order Date', type: 'date' },
      { key: 'expectedDelivery', label: 'Expected Delivery', type: 'date' },
      { key: 'deliveredDate', label: 'Delivered Date', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'accessForms',
    label: 'Access Forms',
    columns: [
      { key: 'formNumber', label: 'Form Number', type: 'string' },
      { key: 'accessType', label: 'Access Type', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'requestedAccess', label: 'Requested Access', type: 'string' },
      { key: 'justification', label: 'Justification', type: 'string' },
      { key: 'employee.firstName', label: 'Employee First', type: 'string' },
      { key: 'employee.lastName', label: 'Employee Last', type: 'string' },
      { key: 'employee.employeeId', label: 'Employee ID', type: 'string' },
      { key: 'lineManager.firstName', label: 'Line Manager', type: 'string' },
      { key: 'hrApprover.firstName', label: 'HR Approver', type: 'string' },
      { key: 'itApprover.firstName', label: 'IT Approver', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'assetIssueForms',
    label: 'Asset Issue Forms',
    columns: [
      { key: 'formNumber', label: 'Form Number', type: 'string' },
      { key: 'purpose', label: 'Purpose', type: 'string' },
      { key: 'terms', label: 'Terms', type: 'string' },
      { key: 'assetsDescription', label: 'Assets Description', type: 'string' },
      { key: 'employee.firstName', label: 'Employee First', type: 'string' },
      { key: 'employee.lastName', label: 'Employee Last', type: 'string' },
      { key: 'employee.employeeId', label: 'Employee ID', type: 'string' },
      { key: 'issuedBy.firstName', label: 'Issued By', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'maintenanceSchedules',
    label: 'Maintenance Schedules',
    columns: [
      { key: 'title', label: 'Title', type: 'string' },
      { key: 'description', label: 'Description', type: 'string' },
      { key: 'frequency', label: 'Frequency', type: 'string' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'priority', label: 'Priority', type: 'string' },
      { key: 'lastPerformedAt', label: 'Last Performed', type: 'date' },
      { key: 'nextDueAt', label: 'Next Due', type: 'date' },
      { key: 'estimatedDuration', label: 'Est. Duration (min)', type: 'number' },
      { key: 'estimatedCost', label: 'Est. Cost', type: 'number' },
      { key: 'asset.assetTag', label: 'Asset Tag', type: 'string' },
      { key: 'asset.name', label: 'Asset Name', type: 'string' },
      { key: 'assignedTo.firstName', label: 'Assigned To', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
  {
    name: 'users',
    label: 'Users',
    columns: [
      { key: 'username', label: 'Username', type: 'string' },
      { key: 'email', label: 'Email', type: 'string' },
      { key: 'firstName', label: 'First Name', type: 'string' },
      { key: 'lastName', label: 'Last Name', type: 'string' },
      { key: 'role', label: 'Role', type: 'string' },
      { key: 'isActive', label: 'Is Active', type: 'boolean' },
      { key: 'lastLogin', label: 'Last Login', type: 'date' },
      { key: 'branch.name', label: 'Branch', type: 'string' },
      { key: 'department.name', label: 'Department', type: 'string' },
      { key: 'createdAt', label: 'Created At', type: 'date' },
    ],
  },
];

interface CustomReportBuilderProps {
  onSaveTemplate?: (config: any) => void;
}

export default function CustomReportBuilder({ onSaveTemplate }: CustomReportBuilderProps) {
  const { toast } = useToast();
  const [selectedDataSource, setSelectedDataSource] = useState<string>('assets');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [groupBy, setGroupBy] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [reportData, setReportData] = useState<any>(null);
  const [templateName, setTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const currentDataSource = dataSources.find(ds => ds.name === selectedDataSource);

  const buildReportMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/reports/custom', {
        dataSource: selectedDataSource,
        columns: selectedColumns,
        filters,
        groupBy: groupBy || undefined,
        sortBy: sortBy || undefined,
        sortOrder,
        limit: 500,
      });
      return response.data.data;
    },
    onSuccess: (data) => {
      setReportData(data);
      toast({ title: 'Report Generated', description: `Found ${data.totalRecords} records` });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to generate report', variant: 'destructive' });
    },
  });

  const handleColumnToggle = (columnKey: string) => {
    setSelectedColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(c => c !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleSelectAllColumns = () => {
    if (!currentDataSource) return;
    const allKeys = currentDataSource.columns.map(c => c.key);
    setSelectedColumns(allKeys);
  };

  const handleClearColumns = () => {
    setSelectedColumns([]);
  };

  const handleExportCSV = () => {
    if (!reportData?.data || reportData.data.length === 0) return;
    
    const headers = reportData.columns.join(',');
    const rows = reportData.data.map((row: any) => 
      reportData.columns.map((col: string) => {
        const val = row[col];
        if (val === null || val === undefined) return '';
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `custom-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportExcel = async () => {
    if (!reportData?.data || reportData.data.length === 0) return;
    setIsExporting(true);
    
    try {
      const response = await api.post('/reports/export/excel', {
        reportType: selectedDataSource,
        title: `Custom Report - ${dataSources.find(ds => ds.name === selectedDataSource)?.label || selectedDataSource}`,
        data: reportData.data,
        columns: reportData.columns,
        filters,
      }, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `custom-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Success', description: 'Excel report downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export Excel', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!reportData?.data || reportData.data.length === 0) return;
    setIsExporting(true);
    
    try {
      const response = await api.post('/reports/export/pdf', {
        reportType: selectedDataSource,
        title: `Custom Report - ${dataSources.find(ds => ds.name === selectedDataSource)?.label || selectedDataSource}`,
        data: reportData.data,
        columns: reportData.columns,
        filters,
      }, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `custom-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Success', description: 'PDF report downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export PDF', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    if (!reportData?.data || reportData.data.length === 0) return;
    setIsExporting(true);
    
    try {
      const response = await api.post('/reports/export/json', {
        reportType: selectedDataSource,
        title: `Custom Report - ${dataSources.find(ds => ds.name === selectedDataSource)?.label || selectedDataSource}`,
        data: reportData.data,
        columns: reportData.columns,
        filters,
      }, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `custom-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Success', description: 'JSON report downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export JSON', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({ title: 'Error', description: 'Please enter a template name', variant: 'destructive' });
      return;
    }

    const config = {
      dataSource: selectedDataSource,
      columns: selectedColumns,
      filters,
      groupBy,
      sortBy,
      sortOrder,
    };

    try {
      await api.post('/reports/templates', {
        name: templateName,
        config,
      });
      toast({ title: 'Success', description: 'Report template saved' });
      setShowSaveDialog(false);
      setTemplateName('');
      onSaveTemplate?.(config);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save template', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Data Source Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5" />
            Select Data Source
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {dataSources.map(ds => (
              <Button
                key={ds.name}
                variant={selectedDataSource === ds.name ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setSelectedDataSource(ds.name);
                  setSelectedColumns([]);
                  setFilters({});
                  setGroupBy('');
                  setSortBy('');
                  setReportData(null);
                }}
              >
                {ds.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Column Selection */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Columns className="w-5 h-5" />
              Select Columns ({selectedColumns.length} selected)
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSelectAllColumns}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={handleClearColumns}>
                Clear
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {currentDataSource?.columns.map(col => (
              <div key={col.key} className="flex items-center space-x-2">
                <Checkbox
                  id={col.key}
                  checked={selectedColumns.includes(col.key)}
                  onCheckedChange={() => handleColumnToggle(col.key)}
                />
                <label
                  htmlFor={col.key}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {col.label}
                </label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Input
                placeholder="Filter by status..."
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sorting & Grouping */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            {sortOrder === 'asc' ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
            Sort & Group
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Sort By</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="">None</option>
                {currentDataSource?.columns.map(col => (
                  <option key={col.key} value={col.key.split('.')[0]}>{col.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Sort Order</Label>
              <div className="flex gap-2">
                <Button
                  variant={sortOrder === 'asc' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortOrder('asc')}
                  className="flex-1"
                >
                  <SortAsc className="w-4 h-4 mr-1" /> Ascending
                </Button>
                <Button
                  variant={sortOrder === 'desc' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortOrder('desc')}
                  className="flex-1"
                >
                  <SortDesc className="w-4 h-4 mr-1" /> Descending
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Group By</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <option value="">None</option>
                {selectedColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button
          variant="outline"
          onClick={() => setShowSaveDialog(true)}
          disabled={selectedColumns.length === 0}
        >
          <Save className="w-4 h-4 mr-2" />
          Save Template
        </Button>
        <Button
          onClick={() => buildReportMutation.mutate()}
          disabled={selectedColumns.length === 0 || buildReportMutation.isPending}
        >
          {buildReportMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Play className="w-4 h-4 mr-2" />
          )}
          Generate Report
        </Button>
      </div>

      {/* Save Template Dialog */}
      {showSaveDialog && (
        <Card className="border-2 border-primary">
          <CardHeader>
            <CardTitle>Save Report Template</CardTitle>
            <CardDescription>Save this configuration for future use</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                placeholder="e.g., Monthly Asset Report"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveTemplate}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {reportData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Report Results</CardTitle>
                <CardDescription>{reportData.totalRecords} records found</CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={isExporting}>
                    {isExporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Export
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportExcel}>
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                    Export to Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportPDF}>
                    <FileText className="w-4 h-4 mr-2 text-red-600" />
                    Export to PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportCSV}>
                    <Download className="w-4 h-4 mr-2 text-blue-600" />
                    Export to CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportJSON}>
                    <FileJson className="w-4 h-4 mr-2 text-yellow-600" />
                    Export to JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={reportData.data}
              columns={reportData.columns.map((col: string) => ({
                key: col,
                header: col.split('.').pop() || col,
              }))}
              maxHeight="max-h-[500px]"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
